// frontend\src\Components\Layout\ChangePassword.jsx
import { useState } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import { toast } from "react-toastify";
import { changePassword } from "../../services/authService";

const ChangePassword = ({ handleClose }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const passwordRegex = {
      lowercase: /[a-z]/,
      uppercase: /[A-Z]/,
      digit: /[0-9]/,
      symbol: /[^A-Za-z0-9]/,
    };

    // Client-side validation
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }
    if (!passwordRegex.lowercase.test(newPassword)) {
      toast.error("Password must contain at least one lowercase letter");
      return;
    }
    if (!passwordRegex.uppercase.test(newPassword)) {
      toast.error("Password must contain at least one uppercase letter");
      return;
    }
    if (!passwordRegex.digit.test(newPassword)) {
      toast.error("Password must contain at least one number");
      return;
    }
    if (!passwordRegex.symbol.test(newPassword)) {
      toast.error("Password must contain at least one special character");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error("New password and Confirm password do not match");
      return;
    }

    // API call to change password
    setIsLoading(true);
    try {
      const response = await changePassword(currentPassword, newPassword);
      
      if (response.success) {
        toast.success("Password updated successfully");
        // Clear form fields
        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
        // Close modal after successful password change
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (error) {
      console.error("Password change error:", error);
      toast.error(error.message || "Failed to update password");
    } finally {
      setIsLoading(false);
    }
  };

  const renderPasswordField = (label, value, setValue, show, setShow) => (
    <div>
      <label className="block text-sm font-medium text-text1 mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 pr-10 "
          required
          disabled={isLoading}
        />
        <div
          className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-500 cursor-pointer"
          onClick={() => setShow(!show)}
        >
          {show ? <Eye size={18} /> : <EyeOff size={18} />}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      onClick={handleClose}
    >
      <div
        className="bg-white p-6 rounded-lg w-[90%] max-w-[1000px] shadow-xl "
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between mb-6">
          <h2 className="sm:text-xl text-sm font-semibold text-gray-800">
            Change Password
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="  text-text1 rounded"
            disabled={isLoading}
          >
            <X />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:gap-16 gap-6 text-sm ">
          {/* Left Section - Form */}
          <div className="w-full sm:w-1/2">
            <form className="space-y-4" onSubmit={handleSubmit}>
              {renderPasswordField(
                "Current Password",
                currentPassword,
                setCurrentPassword,
                showCurrent,
                setShowCurrent
              )}
              {renderPasswordField(
                "New Password",
                newPassword,
                setNewPassword,
                showNew,
                setShowNew
              )}
              {renderPasswordField(
                "Confirm New Password",
                confirmNewPassword,
                setConfirmNewPassword,
                showConfirm,
                setShowConfirm
              )}
            </form>
            <div className="flex justify-start gap-2 pt-4 mt-2">
              <button
                type="submit"
                className={`px-4 py-2 bg-primary text-white rounded ${
                  isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update Password"}
              </button>
            </div>
          </div>

          {/* Right Section - Password Requirements */}
          <div className="w-full sm:w-1/2 text-sm text-text1">
            <h3 className="font-semibold mb-2">Password Requirements:</h3>
            <p className="mb-2 text-text2">
              Ensure that the following requirements are met:
            </p>
            <ul className="list-disc list-inside space-y-1 text-text3">
              <li>At least one lowercase character</li>
              <li>At least one uppercase character</li>
              <li>At least one number</li>
              <li>At least one symbol</li>
              <li>Minimum 8 characters long - the more, the better</li>
              <li>Password and Confirm password should be match</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;